import "./miniLabels.scss";
import { Alert, type GetProps, Tag, Tooltip } from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";

type TooltipProps = GetProps<typeof Tooltip>;

export interface MiniLabelProps extends Pick<TooltipProps, "title" | "style" | "placement"> {
    /** Style of MiniLabel. */
    readonly type: "success" | "info" | "error" | "warning" | "default";

    /** Message text to display. */
    readonly message: React.ReactNode;

    /** Override Icon to display. */
    readonly icon?: React.ReactNode;

    /** Custom Classname. */
    readonly className?: string;

    /** Aria Label for Alert */
    readonly "aria-label"?: string;
}

interface MiniAlertLabelProps extends MiniLabelProps {
    /** Message text to display. */
    readonly message: string;

    /** Rounded Border. */
    readonly rounded?: boolean;

    /** Style of MiniLabel. */
    readonly type: "success" | "info" | "error" | "warning";
}

interface MiniAlertCheckboxProps extends MiniLabelProps {
    /** Message text to display. */
    readonly message: string;

    /** Whether Checked or Not. */
    readonly checked: boolean;

    /** Rounded Border. */
    readonly rounded?: boolean;

    /** On Checked Handler. */
    readonly onChecked: GetProps<typeof Tag.CheckableTag>["onChange"];
}

/** Miniature status label that can be displayed in-line. */
export const MiniAlertLabel = ({
    type,
    message,
    icon,
    className,
    rounded = true,
    "aria-label": ariaLabel,
    ...props
}: Readonly<MiniAlertLabelProps>) => (
    <Tooltip placement="topLeft" {...props}>
        <div className={`mini-label-wrapper ${className ?? ""}`}>
            <Alert
                className={`mini-label mini-alert-label ${rounded ? "mini-label-rounded" : ""} mini-alert-label-${type}`}
                type={type}
                icon={icon}
                showIcon
                message={message}
                aria-label={ariaLabel}
            />
        </div>
    </Tooltip>
);

/** Miniature status checkbox that can be displayed in-line. */
export const MiniAlertCheckbox = ({
    type,
    message,
    checked,
    onChecked,
    icon,
    className,
    rounded = true,
    "aria-label": ariaLabel,
    ...props
}: Readonly<MiniAlertCheckboxProps>) => (
    <Tooltip placement="top" {...props}>
        <div
            role="checkbox"
            aria-checked={checked}
            aria-label={message}
            className={`mini-label-wrapper ${className ?? ""}`}
        >
            <Tag.CheckableTag
                className={`mini-label mini-alert-checkboxes ${rounded ? "mini-label-rounded" : ""} mini-alert-checkboxes-${type} ${
                    checked ? "mini-alert-checkboxes-checked" : "mini-alert-checkboxes-unchecked"
                }`}
                checked={checked}
                onChange={onChecked}
                aria-label={ariaLabel}
            >
                {icon ?? defaultIcons[type]}
                <span>{message}</span>
            </Tag.CheckableTag>
        </div>
    </Tooltip>
);

/** Miniature status message that can be displayed in-line. */
export const MiniAlertText = ({
    type,
    message,
    icon,
    className,
    placement,
    "aria-label": ariaLabel,
    ...props
}: Readonly<MiniLabelProps>) => (
    <Tooltip placement={placement ?? "top"} {...props}>
        <div className={`mini-label-wrapper ${className ?? ""}`}>
            <div className={`mini-label mini-alert-text mini-alert-text-${type}`} aria-label={ariaLabel} role="alert">
                {icon ?? defaultIcons[type]}
                <span>{message}</span>
            </div>
        </div>
    </Tooltip>
);

const defaultIcons = {
    info: <InfoCircleOutlined />,
    success: <CheckCircleOutlined />,
    warning: <ExclamationCircleOutlined />,
    error: <CloseCircleOutlined />,
    default: <ClockCircleOutlined />,
};
